import { useState, useEffect, useCallback } from 'react'
import { useWallet, useConnection } from '@solana/wallet-adapter-react'
import { LAMPORTS_PER_SOL } from '@solana/web3.js'
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Copy,
  Eye,
  EyeOff,
  Settings,
  Shield,
  Zap,
  Coins,
  Clock,
  CheckCircle2
} from 'lucide-react'
import { cn } from '../lib/utils'
import WalletConnectButton from './WalletConnectButton'

/**
 * Interface for wallet balance information
 * Defines structure for different token balances
 */
interface WalletBalance {
  symbol: string
  amount: number
  value: number
  change24h: number
  icon?: string
}

/**
 * Props for the WalletPanel component
 * Note: showTransactions prop removed for privacy - Recent Transactions section has been removed
 */
interface WalletPanelProps {
  className?: string
  showBalanceValue?: boolean
  autoRefresh?: boolean
  refreshInterval?: number
}

/**
 * Enhanced WalletPanel Component - Premium wallet interface with glassmorphism design
 * 
 * This component provides:
 * - Professional glassmorphism wallet interface
 * - Real-time balance tracking with animations
 * - Interactive balance cards with hover effects
 * - Responsive design optimized for all devices
 * - Accessibility-compliant interactions
 * - Premium DeFi platform aesthetic
 * - NEW: Uses WalletConnectButton that shows TOS before wallet modal
 * 
 * Note: Recent Transactions section has been removed for privacy reasons
 * Note (2026-01-13): Send/Receive/Stake buttons removed as they were non-functional
 * Note (2026-01-18): Updated to use WalletConnectButton for improved TOS UX flow
 */
export function WalletPanel({
  className,
  showBalanceValue = true,
  autoRefresh = true,
  refreshInterval = 30000
}: WalletPanelProps): JSX.Element {
  const { connected, publicKey } = useWallet()
  const { connection } = useConnection()
  const [balance, setBalance] = useState<number>(0)
  const [balanceHidden, setBalanceHidden] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date())
  const [balanceChange, setBalanceChange] = useState<number>(0)
  const [tokenBalances, setTokenBalances] = useState<WalletBalance[]>([])
  // Live SOL/USD price fetched from CoinGecko
  const [solPrice, setSolPrice] = useState<number>(0)

  /**
   * Fetch the current SOL/USD price from CoinGecko's free public API.
   * No API key required for basic price lookups.
   * Called once on mount and whenever the balance is refreshed.
   */
  const fetchSolPrice = useCallback(async () => {
    try {
      const res = await fetch(
        'https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd',
        { headers: { Accept: 'application/json' } }
      )
      if (!res.ok) throw new Error(`CoinGecko returned ${res.status}`)
      const data = await res.json()
      const price = data?.solana?.usd
      if (typeof price === 'number' && price > 0) {
        setSolPrice(price)
      }
    } catch (error) {
      console.error('Error fetching SOL price:', error)
      // Leave the previous price value in place rather than resetting to 0
    }
  }, [])

  /**
   * Fetch wallet balance from the Solana network
   * Updates the current SOL balance and tracks changes
   */
  const fetchBalance = async (showRefreshing = false) => {
    if (!connected || !publicKey) return

    if (showRefreshing) setRefreshing(true)

    try {
      const lamports = await connection.getBalance(publicKey)
      const newBalance = lamports / LAMPORTS_PER_SOL
      
      // Calculate balance change (mock calculation)
      const change = newBalance - balance
      setBalanceChange(change)
      setBalance(newBalance)
      
      // Update token balances with real SOL balance and live price
      setTokenBalances([
        {
          symbol: 'SOL',
          amount: newBalance,
          value: newBalance * solPrice,
          change24h: 0,
          icon: '◎'
        }
      ])
      
      setLastUpdated(new Date())
    } catch (error) {
      console.error('Error fetching balance:', error)
    } finally {
      setRefreshing(false)
    }
  }

  /**
   * Fetch SOL price once on mount, then refresh every 60 seconds.
   * Runs independently of wallet connection so price is ready when needed.
   */
  useEffect(() => {
    fetchSolPrice()
    const priceInterval = setInterval(fetchSolPrice, 60_000)
    return () => clearInterval(priceInterval)
  }, [fetchSolPrice])

  /**
   * Set up auto-refresh for balance updates
   */
  useEffect(() => {
    if (connected && publicKey) {
      fetchBalance()

      if (autoRefresh) {
        const interval = setInterval(() => {
          fetchBalance(true)
        }, refreshInterval)

        return () => clearInterval(interval)
      }
    }
    return undefined
  }, [connected, publicKey, autoRefresh, refreshInterval])

  /**
   * Format currency values for display
   */
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value)
  }

  /**
   * Format SOL amounts for display
   */
  const formatSOL = (amount: number): string => {
    return `${amount.toFixed(4)} SOL`
  }

  /**
   * Format wallet address for display
   */
  const formatAddress = (address: string): string => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`
  }

  /**
   * Copy text to clipboard with feedback
   */
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      // Add toast notification here if available
    } catch (err) {
      console.error('Failed to copy text: ', err)
    }
  }

  // Show connection interface if not connected
  if (!connected) {
    return (
      <div className={cn("glass-card p-8 text-center", className)}>
        <div className="space-y-6">
          <div className="w-16 h-16 bg-cyber-gradient rounded-cyber flex items-center justify-center mx-auto">
            <Wallet className="w-8 h-8 text-dark-bg" />
          </div>
          
          <div>
            <h3 className="text-xl font-header text-gradient-cyber mb-2">
              Connect Your Wallet
            </h3>
            <p className="text-text-secondary max-w-sm mx-auto">
              Connect your Solana wallet to view balances, manage funds, and interact with pools.
            </p>
          </div>

          <div className="space-y-4">
            {/* Custom WalletConnectButton that shows TOS before wallet modal */}
            <WalletConnectButton 
              connectLabel="Connect Wallet"
              variant="primary"
              size="lg"
              showDropdown={false}
              className="mx-auto"
            />
            
            <div className="flex items-center justify-center space-x-4 text-xs text-text-muted">
              <div className="flex items-center space-x-1">
                <Shield className="w-3 h-3" aria-hidden="true" />
                <span>Secure</span>
              </div>
              <div className="flex items-center space-x-1">
                <Zap className="w-3 h-3" aria-hidden="true" />
                <span>Fast</span>
              </div>
              <div className="flex items-center space-x-1">
                <CheckCircle2 className="w-3 h-3" aria-hidden="true" />
                <span>Trusted</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={cn("space-y-4 lg:space-y-6", className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-cyber-gradient rounded-cyber flex items-center justify-center">
            <Wallet className="w-5 h-5 text-dark-bg" />
          </div>
          <div>
            <h3 className="text-xl font-header text-gradient-cyber">Wallet</h3>
            <div className="flex items-center space-x-2 text-sm text-text-muted">
              <code className="font-mono">
                {publicKey ? formatAddress(publicKey.toBase58()) : 'Not connected'}
              </code>
              {publicKey && (
                <button
                  onClick={() => copyToClipboard(publicKey.toBase58())}
                  className="hover:text-cyber-cyan transition-colors duration-200"
                  aria-label="Copy wallet address"
                >
                  <Copy className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => setBalanceHidden(!balanceHidden)}
            className="cyber-button-secondary p-2"
            aria-label={balanceHidden ? 'Show balance' : 'Hide balance'}
          >
            {balanceHidden ? (
              <EyeOff className="w-4 h-4" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
          </button>

          <button
            onClick={() => fetchBalance(true)}
            disabled={refreshing}
            className="cyber-button-secondary p-2 disabled:opacity-50"
            aria-label="Refresh balance"
          >
            <RefreshCw className={cn(
              "w-4 h-4",
              refreshing && "animate-spin"
            )} />
          </button>

          <button 
            className="cyber-button-secondary p-2"
            aria-label="Wallet settings"
          >
            <Settings className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Main Balance Card */}
      <div className="metric-card group">
        <div className="flex items-start justify-between mb-6">
          <div className="space-y-2">
            <div className="flex items-center space-x-2 text-text-muted">
              <Coins className="w-4 h-4" aria-hidden="true" />
              <span className="text-sm">Total Balance</span>
            </div>
            
            <div className="space-y-1">
              {balanceHidden ? (
                <div className="metric-value">••••••</div>
              ) : (
                <div className="metric-value group-hover:text-glow animate-scale-in">
                  {formatSOL(balance)}
                </div>
              )}
              
              {showBalanceValue && !balanceHidden && solPrice > 0 && (
                <div className="text-lg text-text-secondary">
                  ≈ {formatCurrency(balance * solPrice)}
                </div>
              )}
            </div>
          </div>

          {balanceChange !== 0 && !balanceHidden && (
            <div className={cn(
              "flex items-center space-x-1 px-3 py-1.5 rounded-cyber text-sm font-medium",
              balanceChange >= 0 
                ? "bg-status-success/20 text-status-success" 
                : "bg-status-error/20 text-status-error"
            )}>
              {balanceChange >= 0 ? (
                <TrendingUp className="w-4 h-4" aria-hidden="true" />
              ) : (
                <TrendingDown className="w-4 h-4" aria-hidden="true" />
              )}
              <span>
                {balanceChange >= 0 ? '+' : ''}{formatSOL(balanceChange)}
              </span>
            </div>
          )}
        </div>

        {/* 
         * UI FIX (2026-01-13): Removed non-functional Quick Actions buttons
         * Previously contained Send, Receive, and Stake buttons that had no functionality
         * These can be re-added when the functionality is implemented
         */}
      </div>

      {/* Token Balances - Only show if more than just SOL */}
      {tokenBalances.length > 1 && (
        <div className="space-y-4">
          <h4 className="text-lg font-header text-cyber-cyan">Token Balances</h4>
          
          <div className="grid gap-3 lg:gap-4">
            {tokenBalances.map((token) => (
              <div key={token.symbol} className="glass-card p-4 hover:scale-102 transition-all duration-300 group cursor-pointer">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-cyber-cyan/20 rounded-cyber flex items-center justify-center">
                      <span className="text-cyber-cyan font-bold">
                        {token.icon || token.symbol[0]}
                      </span>
                    </div>
                    
                    <div>
                      <div className="font-medium text-text-primary">
                        {token.symbol}
                      </div>
                      <div className="text-sm text-text-muted">
                        {balanceHidden ? '••••' : token.amount.toLocaleString()}
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="font-mono text-text-primary">
                      {balanceHidden ? '••••' : formatCurrency(token.value)}
                    </div>
                    <div className={cn(
                      "text-xs flex items-center justify-end space-x-1",
                      token.change24h >= 0 ? "text-status-success" : "text-status-error"
                    )}>
                      {token.change24h >= 0 ? (
                        <TrendingUp className="w-3 h-3" aria-hidden="true" />
                      ) : (
                        <TrendingDown className="w-3 h-3" aria-hidden="true" />
                      )}
                      <span>
                        {token.change24h >= 0 ? '+' : ''}{token.change24h.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Connection Status Footer */}
      <div className="flex items-center justify-between text-xs text-text-muted pt-4 border-t border-dark-border/30">
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-status-success rounded-full animate-pulse" aria-hidden="true" />
          <span>Connected to Devnet</span>
        </div>
        
        <div className="flex items-center space-x-2">
          <Clock className="w-3 h-3" aria-hidden="true" />
          <span>Last updated: {lastUpdated.toLocaleTimeString()}</span>
        </div>
      </div>
    </div>
  )
}

export default WalletPanel
